import { assert } from '@core/utils';
import type { InferDataFromTag, QueryKey } from '@tanstack/react-query';

import {
  AuthQueryOptions,
  UnauthInfiniteQueryOptions,
  UnauthQueryOptions,
} from '@/api';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

import {
  DEMO_EVENTS,
  DEMO_GIFT_PRODUCTS,
  DEMO_LINK_LIST,
  DEMO_LINK_PAGES,
  DEMO_REGIONS,
  DEMO_REPORT,
  GUEST_MEMBER,
} from './demo';
import type { PartnersLinkQuery, PartnersReportQuery } from './types';

type LiveQuery = { queryKey: QueryKey & readonly [{ _id: string }] };

const GIFT_PAGE_SIZE = 20;

//* 키의 _id를 바꿔 실제 API 키 무효화·낙관 갱신이 예시 캐시에 닿지 않게 한다
const demoQueryOf = <Options extends LiveQuery>(
  live: Options,
  response: InferDataFromTag<unknown, Options['queryKey']>,
): Options => ({
  ...live,
  queryKey: [
    { ...live.queryKey[0], _id: `partnersDemo:${live.queryKey[0]._id}` },
  ],
  queryFn: () => response,
  initialData: response,
  staleTime: Infinity,
});

export const contextOf = <Context>({ context }: { context?: Context }) => {
  assert(context);

  return context;
};

export const memberQueryOf = (isGuest: boolean) => {
  const live = AuthQueryOptions['get:/wello/v2/auth/partners/me']();

  return isGuest ? demoQueryOf(live, { context: GUEST_MEMBER }) : live;
};

export const partnersQueriesOf = (isDemo: boolean) => ({
  links: (query: PartnersLinkQuery) => {
    const live = AuthQueryOptions['get:/wello/v2/auth/partners/links']({
      query,
    });

    return isDemo ? demoQueryOf(live, { context: DEMO_LINK_LIST }) : live;
  },
  report: (query: PartnersReportQuery) => {
    const live = AuthQueryOptions['get:/wello/v2/auth/partners/report']({
      query,
    });

    return isDemo ? demoQueryOf(live, { context: DEMO_REPORT }) : live;
  },
  linkPages: () => {
    const live = AuthQueryOptions['get:/wello/v2/auth/partners/link-pages']();

    return isDemo
      ? demoQueryOf(live, { context: { contents: DEMO_LINK_PAGES } })
      : live;
  },
  giftProducts: (keyword: string) => {
    const live = filledInfiniteQueryOptions(
      UnauthInfiniteQueryOptions['get:/wello/v2/allows/query/gift-products']({
        query: { searchKeyword: keyword, page: 1, size: GIFT_PAGE_SIZE },
      }),
    );
    const contents = DEMO_GIFT_PRODUCTS.filter((gift) =>
      gift.productName?.includes(keyword),
    );
    const demoPage = {
      context: {
        contents,
        totalCount: contents.length,
        size: GIFT_PAGE_SIZE,
        page: 1,
      },
    };

    return isDemo
      ? {
          ...demoQueryOf(live, { pages: [demoPage], pageParams: [1] }),
          queryFn: () => demoPage,
        }
      : live;
  },
  regions: () => {
    const live =
      UnauthQueryOptions['get:/wello/v2/allows/home-love-gov-code/child']();

    return isDemo
      ? demoQueryOf(live, { context: { contents: DEMO_REGIONS } })
      : live;
  },
  events: () => {
    const live = UnauthQueryOptions[
      'get:/wello/v2/allows/home/institution-event'
    ]({ query: { excludeEndedYN: true, entry: 'WELLO' } });

    return isDemo
      ? demoQueryOf(live, {
          context: {
            contents: DEMO_EVENTS,
            page: 1,
            size: DEMO_EVENTS.length,
            totalCount: DEMO_EVENTS.length,
          },
        })
      : live;
  },
});

export type PartnersQueries = ReturnType<typeof partnersQueriesOf>;
